import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
  useGetQuizQuestionDetailsQuery,
  useUpdateQuizQuestionMutation,
} from "../../slices/quizQuestionApiSlice";

const QuizQuestionEditScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(1);
  const [marks, setMarks] = useState(1);
  const [isActive, setIsActive] = useState(true);

  const {
    data: quizQuestion,
    isLoading,
    error,
  } = useGetQuizQuestionDetailsQuery(id);

  const [updateQuizQuestion, { isLoading: loadingUpdate }] =
    useUpdateQuizQuestionMutation();

  useEffect(() => {
    if (quizQuestion) {
      setOrder(quizQuestion.order || 1);
      setMarks(quizQuestion.marks || 1);
      setIsActive(quizQuestion.isActive);
    }
  }, [quizQuestion]);

  const submitHandler = async (e) => {
    e.preventDefault();

    try {
      await updateQuizQuestion({
        quizQuestionId: id,
        order: Number(order),
        marks: Number(marks),
        isActive,
      }).unwrap();

      toast.success("Quiz question mapping updated successfully");

      navigate(`/quizzes/${quizQuestion.quiz}/questions`);
    } catch (err) {
      toast.error(
        err?.data?.message ||
          err?.error ||
          "Failed to update quiz question mapping",
      );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <Link
          to={`/quizzes/${quizQuestion?.quiz}/questions`}
          className="inline-flex items-center gap-2 mb-6 rounded-xl bg-white px-4 py-2 text-blue-600 shadow hover:bg-blue-50 transition"
        >
          ← Back to Quiz Questions
        </Link>

        <div className="rounded-3xl border border-white/30 bg-white/80 p-6 shadow-2xl backdrop-blur-lg md:p-10">
          <div className="mb-8 text-center">
            <h1 className="text-3xl font-bold text-gray-800 md:text-4xl">
              Edit Question Mapping
            </h1>

            <p className="mt-2 text-gray-500">
              Update question order, marks and status
            </p>
          </div>

          {isLoading && (
            <div className="mb-6 rounded-xl bg-blue-100 p-4 text-center text-blue-700">
              Loading question mapping...
            </div>
          )}

          {loadingUpdate && (
            <div className="mb-6 rounded-xl bg-blue-100 p-4 text-center text-blue-700">
              Updating question mapping...
            </div>
          )}

          {error && (
            <div className="mb-6 rounded-xl bg-red-100 p-4 text-center text-red-700">
              {error?.data?.message ||
                error?.error ||
                "Failed to load question mapping"}
            </div>
          )}

          {!isLoading && quizQuestion && (
            <form onSubmit={submitHandler} className="space-y-6">
              {/* Question Preview */}

              <div className="rounded-2xl border bg-gray-50 p-5">
                <h2 className="mb-3 text-sm font-semibold uppercase text-gray-500">
                  Question
                </h2>

                <p className="font-semibold text-gray-800">
                  {quizQuestion.question?.questionText ||
                    quizQuestion.question?.text ||
                    quizQuestion.question?.title ||
                    "Question unavailable"}
                </p>
              </div>

              {/* Order */}

              <div>
                <label className="mb-2 block text-sm font-semibold text-gray-700">
                  Question Order
                </label>

                <input
                  type="number"
                  min="1"
                  value={order}
                  onChange={(e) => setOrder(e.target.value)}
                  className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Marks */}

              <div>
                <label className="mb-2 block text-sm font-semibold text-gray-700">
                  Marks
                </label>

                <input
                  type="number"
                  min="0"
                  value={marks}
                  onChange={(e) => setMarks(e.target.value)}
                  className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Active Status */}

              <div className="flex items-center gap-3 rounded-xl border bg-gray-50 p-4">
                <input
                  id="isActive"
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="h-5 w-5 rounded text-blue-600 focus:ring-blue-500"
                />

                <label
                  htmlFor="isActive"
                  className="font-semibold text-gray-700"
                >
                  Active Question Mapping
                </label>
              </div>

              {/* Buttons */}

              <div className="flex flex-col gap-4 sm:flex-row">
                <button
                  type="submit"
                  disabled={loadingUpdate}
                  className="flex-1 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 py-3.5 text-lg font-semibold text-white shadow-lg transition hover:from-blue-700 hover:to-indigo-700 disabled:opacity-60"
                >
                  {loadingUpdate ? "Updating..." : "Update Mapping"}
                </button>

                <Link
                  to={`/quizzes/${quizQuestion.quiz}/questions`}
                  className="flex-1 rounded-xl border border-gray-300 bg-white py-3.5 text-center text-lg font-semibold text-gray-700 transition hover:bg-gray-100"
                >
                  Cancel
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuizQuestionEditScreen;
